import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  useModal,
  useMoment,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { ParagraphMedium } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { PATHS_REPORTS_TABLE_COLUMNS } from '../../constants/reports';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { deletePathsReportFile, getPathsReportFile, getPathsReportFiles } from '../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../storage/app';
import { updateReportsState } from '../../storage/reports';
import { PathsReport } from '../../types/reports';
import { formatDurationFromSeconds } from '../../utils/dates';
import { getFormattedNumber } from '../../utils/numbers';
import ConfirmModal from '../shared/ConfirmModal';
import { StyledPaginatedTable } from '../shared/StyledPaginatedTable';
import { StyledTooltip } from '../ui/StyledTooltip';

export function PathsDownloadList() {
  const { css, classes, theme } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [reports, setReports] = useState<PathsReport[]>();
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { formatDateFromTs } = useMoment();
  const [loading, setLoading] = useState(true);
  const { showModal, closeModal, openModal } = useModal();
  const [reportToDelete, setReportToDelete] = useState<string>();
  const [updateReports, setUpdateReports] = useRecoilState(updateReportsState);
  const { showDeletionFeedback, showFailFeedback } = useFeedback();
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);

  useUpdateEffect(() => {
    setPage(0);
  }, [updateReports]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    setLoading(true);
    const loadPathsReports = async () => {
      if (!apiParams) return;
      const { error, reports, total } = await getPathsReportFiles(
        apiParams.clientId,
        page,
        isAllWorkspacesSelected ? undefined : apiParams.subClientId,
      );
      setReports(reports);
      setTotalItems(total ?? 0);
      if (error) {
        showFailFeedback(error || 'Ocurrió un error al cargar los reportes, inténtalo más tarde.');
      }
      setLoading(false);
    };
    loadPathsReports();
  }, [page]);

  const downloadPathsReport = async (reportId: string) => {
    if (!apiParams) return;
    const { url, error } = await getPathsReportFile(
      reportId,
      apiParams.clientId,
      isAllWorkspacesSelected ? undefined : apiParams.subClientId,
    );
    if (!error && url) {
      window.open(url, '_blank');
    } else {
      showFailFeedback(error || 'Ocurrió un error al descargar el reporte, inténtalo más tarde.');
    }
  };

  const onDeleteReport = async () => {
    if (!apiParams || !reportToDelete) return;
    const { error } = await deletePathsReportFile(
      reportToDelete,
      apiParams.clientId,
      isAllWorkspacesSelected ? undefined : apiParams.subClientId,
    );
    if (!error) {
      setUpdateReports(!updateReports);
      showDeletionFeedback(FEEDBACK.deleted(FEEDBACK_PREFIXES.report, reportToDelete));
      closeModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedDeletion(FEEDBACK_PREFIXES.report, reportToDelete));
    }
  };

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
      {!reports?.length && !loading && <EmptyState title="Todavía no tienes archivos para descargar" />}
      {!reports?.length && loading && <Loading />}
      {!!reports?.length && (
        <StyledPaginatedTable
          columns={PATHS_REPORTS_TABLE_COLUMNS}
          data={reports.map((report, i) => {
            const name = report.asset?.label || report.group?.name || '-';
            return {
              items: [
                name,
                formatDateFromTs(report.from),
                formatDateFromTs(report.to),
                report.filters?.minSeconds ? formatDurationFromSeconds(report.filters.minSeconds, true) : '-',
                getFormattedNumber(report.filters?.minMeters, { suffix: 'm' }),
                report.statusDescription,
                report.processedAt ? formatDateFromTs(report.processedAt) : '-',
                <FlexRow
                  key={`details-${report._id}`}
                  gap={theme.sizing.scale0}
                >
                  <StyledTooltip
                    content={'Descargar'}
                    showArrow={false}
                  >
                    <Button
                      size="compact"
                      shape="square"
                      onClick={() => downloadPathsReport(report._id)}
                      kind="tertiary"
                      disabled={report.status !== 2}
                    >
                      <MaterialIcon name="download_for_offline" />
                    </Button>
                  </StyledTooltip>
                  <StyledTooltip
                    content={'Eliminar'}
                    showArrow={false}
                  >
                    <Button
                      size="compact"
                      shape="square"
                      onClick={() => {
                        setReportToDelete(report._id);
                        openModal();
                      }}
                      kind="tertiary"
                    >
                      <MaterialIcon name="delete" />
                    </Button>
                  </StyledTooltip>
                </FlexRow>,
              ],
            };
          })}
          totalItems={totalItems}
          page={page}
          onPageChange={setPage}
          loading={loading}
        />
      )}
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onDeleteReport}
        confirmText="Eliminar"
        description={<ParagraphMedium>¿Desea eliminar este reporte?</ParagraphMedium>}
      />
    </FlexColumn>
  );
}
